// hooks/useWeather.ts
import { useState, useCallback } from 'react';
import axios from 'axios';
import type { City, CityWeatherData, Forecast, OpenMeteoData, OpenMeteoHourly } from '../interfaces/weather.types';
import { getWeatherIcon } from '../helpers/weatherIcons';
import { getDateRangeForApi } from '../helpers/dateFormat';


// Conversión MJ/m2 diario → promedio W/m2
const mjToAverageWm2 = (mj: number | null): number | null => {
  if (mj === null || mj === undefined) return null;
  return Math.round(mj / 0.0864);
};

const getAverageHumidityForDay = (hourly: OpenMeteoHourly, dayIndex: number): number | null => {
  if (!hourly || !hourly.relative_humidity_2m) return null;

  const hoursPerDay = 24;
  const startIndex = (dayIndex) * hoursPerDay;
  const endIndex = startIndex + hoursPerDay;

  const dayHumidity = hourly.relative_humidity_2m.slice(startIndex, endIndex);
  const validHumidity = dayHumidity.filter(h => h !== null && h !== undefined);

  if (validHumidity.length === 0) return null;

  return Math.round(
    validHumidity.reduce((sum, h) => sum + h, 0) / validHumidity.length
  );
};

const processCityWeather = (city: City, apiData: OpenMeteoData): CityWeatherData | null => {
  if (!apiData ) return null;

  const { daily, hourly } = apiData;

  // Pronóstico histórico de los últimos 7 días
  const historical: Forecast[] = [];
  if (daily && daily.time) {
    for (let i = 0; i < 7; i++) { // Tomamos los 7 días históricos
      if (daily.time[i]) {

        historical.push({
          date: daily.time[i],
          max_temperature: Math.round(daily.temperature_2m_max[i]),
          min_temperature: Math.round(daily.temperature_2m_min[i]),
          humidity: getAverageHumidityForDay(hourly, i),
          wind_speed: daily.wind_speed_10m_max ? daily.wind_speed_10m_max[i] : null,
          icon: getWeatherIcon(daily.weather_code[i], true), // Para el histórico, asumimos icono de día
          precipitation: daily.precipitation_sum ? daily.precipitation_sum[i] : null,

          solar_radiation: daily.shortwave_radiation_sum
            ? mjToAverageWm2(daily.shortwave_radiation_sum[i])
            : null,
        });
      }
    }
  }


  return {
    city: city.city,
    country: city.country,
    ggn_number: city.ggn_number || "",
    latitude: city.latitude,
    longitude: city.longitude,
    historical: historical,
    date: city.date ? new Date(city.date) : new Date(),
  };
};

export const useWeather = () => {
  const [weatherData, setWeatherData] = useState<CityWeatherData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchWeatherData = useCallback(async (cities: City[]) => {

    try {
      setLoading(true);

      const weatherPromises = cities.map(city => {

        if (city.date) {
          const { start_date, end_date } = getDateRangeForApi(
            new Date(city.date),
            7 // últimos 7 días incluyendo la fecha objetivo
          );

          return axios.get('https://archive-api.open-meteo.com/v1/archive', {
            params: {
              latitude: city.latitude,
              longitude: city.longitude,
              start_date,
              end_date,

              daily: [
                'temperature_2m_max',
                'temperature_2m_min',
                'apparent_temperature_max',
                'apparent_temperature_min',
                'precipitation_sum',
                'wind_speed_10m_max',
                'weather_code',
                'rain_sum',
                'showers_sum',
                'snowfall_sum',
                'shortwave_radiation_sum'
              ],

              hourly: [
                'temperature_2m',
                'relative_humidity_2m',
                'wind_speed_10m',
                'precipitation',
                'rain',
                'showers',
                'snowfall',
                'shortwave_radiation'
              ],

              timezone: 'auto',
            },
            timeout: 10000
          });

        } else {
          // Si no hay fecha específica, usamos el comportamiento original
          return axios.get('https://api.open-meteo.com/v1/forecast', {
            params: {
              latitude: city.latitude,
              longitude: city.longitude,
              current: [
                'temperature_2m',
                'relative_humidity_2m',
                'wind_speed_10m',
                'weather_code',
                'is_day',
                'precipitation',
                'rain',
                'showers',
                'snowfall',
                'shortwave_radiation'
              ],
              hourly: [
                'temperature_2m',
                'relative_humidity_2m',
                'wind_speed_10m',
                'precipitation',
                'rain',
                'showers',
                'snowfall',
                'shortwave_radiation'
              ],
              daily: [
                'temperature_2m_max',
                'temperature_2m_min',
                'apparent_temperature_max',
                'apparent_temperature_min',
                'precipitation_sum',
                'wind_speed_10m_max',
                'weather_code',
                'rain_sum',
                'showers_sum',
                'snowfall_sum',
                'shortwave_radiation_sum'
              ],
              timezone: 'auto',
              past_days: 7
            },
            timeout: 10000
          });
        }
      }
      );

      // Esto debe ser Promise.all(weatherPromises)
      const responses = await Promise.all(weatherPromises);

      const processedData: CityWeatherData[] = responses
        .map((response, index) => {
          return processCityWeather(cities[index], response.data);
        })
        .filter((data): data is CityWeatherData => data !== null);

      setWeatherData(processedData);
      setLastUpdated(new Date()); // Actualizamos el estado con un objeto Date
      setError(null);
      return processedData

    } catch (err: any) {
      setError(`General error: ${err?.message || 'Unknown error'}`);
      console.error('Error fetching weather data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    weatherData,
    loading,
    error,
    lastUpdated,
    fetchWeatherData
  };
};